import type { MetaFunction } from 'react-router'
import { Link } from 'react-router'
import { useState } from 'react'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { BreadcrumbSchema, FAQSchema } from '~/components/structured-data'
import { ArticleSchema } from '~/components/seo-content'
import { FloatingCTA } from '~/components/floating-cta'
import { InlineLeadCapture } from '~/components/inline-lead-capture'
import { TrustStrip } from '~/components/trust-strip'
import { STATIC_PAGE_HEADERS } from '~/lib/cache.server'
import {
  Flame, ArrowRight, CheckCircle, Calculator, AlertTriangle,
  Euro, Home, Users, Shield, Thermometer,
  Snowflake, DoorOpen, Sun,
} from 'lucide-react'

const MPR_FAQS = [
  { question: "Qui peut bénéficier de MaPrimeRénov' en 2026 ?", answer: "Tous les propriétaires occupants ou bailleurs d'un logement de plus de 15 ans (5 ans pour le remplacement d'une chaudière au fioul). Les copropriétés bénéficient aussi de MaPrimeRénov' Copropriétés. Les revenus fiscaux du foyer déterminent la couleur du dossier (Bleu, Jaune, Violet, Rose) et donc le montant de l'aide." },
  { question: "Quels travaux sont éligibles à MaPrimeRénov' ?", answer: "Pompes à chaleur (air/eau, géothermique), isolation des combles et planchers, fenêtres double vitrage en remplacement de simple vitrage, ventilation double flux, chauffe-eau thermodynamique et solaire, audit énergétique. L'isolation des murs (ITE et ITI) et les chaudières biomasse ne sont plus financées dans le parcours par geste depuis le 1er janvier 2026 — uniquement via le parcours accompagné. Les travaux doivent être réalisés par un artisan certifié RGE." },
  { question: "Comment se calcule le montant de MaPrimeRénov' ?", answer: "Le montant dépend de 3 facteurs : type de travaux (barème spécifique par opération), revenus du foyer (4 catégories de la plus modeste au plus aisé : Bleu, Jaune, Violet, Rose), et localisation (Île-de-France ou autre). Pour une rénovation globale d'ampleur, des bonus s'ajoutent : sortie de passoire thermique (+1 000 € Bleu/Jaune), BBC rénovation (+500 €). Plafond cumulatif : 20 000 € par logement sur 5 ans." },
  { question: "Peut-on cumuler MaPrimeRénov' avec d'autres aides ?", answer: "Oui : CEE (Certificats d'Économies d'Énergie), éco-PTZ (jusqu'à 50 000 € sans intérêts), TVA réduite à 5,5 %, aides locales des collectivités. Les aides sont déduites du montant des travaux dans cet ordre : MaPrimeRénov' → CEE → reste à charge éco-PTZ. Pour un ménage Bleu remplaçant une chaudière fioul par une PAC, le cumul peut couvrir jusqu'à 90 % du coût total." },
  { question: "Comment faire une demande MaPrimeRénov' en 2026 ?", answer: "1) Créer un compte sur france-renov.gouv.fr. 2) Faire établir un devis par un artisan RGE certifié. 3) CRUCIAL : déposer le devis sur france-renov.gouv.fr AVANT signature — toute signature préalable annule l'aide. 4) Attendre l'accord de subvention (3 à 6 semaines). 5) Réaliser les travaux. 6) Transmettre la facture. L'aide est versée sous 4 à 8 semaines après les travaux. Acompte de 70 % possible avant travaux pour les ménages Bleu et Jaune." },
  { question: "Quelles sont les nouveautés MaPrimeRénov' 2026 ?", answer: "Trois changements majeurs depuis le 1er janvier 2026 : 1) L'isolation des murs (ITE et ITI) et les chaudières biomasse ne sont plus financées dans le parcours par geste — uniquement via le parcours accompagné. 2) La catégorie Rose (revenus supérieurs) accède désormais au parcours accompagné pour les rénovations d'ampleur. 3) Réouverture du guichet le 23 février 2026 après gel budgétaire. Plafond cumulatif maintenu à 20 000 €/logement sur 5 ans." },
  { question: "Que faire en cas de refus ou litige avec l'artisan ?", answer: "En cas de refus de l'Anah, recours gracieux possible auprès du préfet du département dans les 2 mois. Le motif le plus fréquent (devis signé avant dépôt) est rédhibitoire. En cas de litige avec l'artisan (malfaçon, abandon de chantier, surcoût injustifié), AfficheServices propose une médiation gratuite. Si la médiation échoue, recours auprès du Médiateur de la consommation (obligatoire avant tribunal), DGCCRF en cas de pratique commerciale trompeuse, ou Médiateur de l'énergie pour les litiges spécifiques aux pompes à chaleur." },
]

export function headers() { return STATIC_PAGE_HEADERS }

export const meta: MetaFunction = () => [
  { title: 'MaPrimeRénov\' 2026 — Montants, conditions, simulateur gratuit' },
  { name: 'description', content: 'Barèmes MaPrimeRénov\' 2026 par travaux (PAC, isolation, fenêtres) et revenus (Bleu/Jaune/Violet/Rose). Simulateur d\'aide instantané + artisans RGE Qualibat vérifiés sous 48h.' },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/maprimenov' },
]

// Barèmes simplifiés MaPrimeRénov' 2026 (source: ANAH)
const BAREME: Array<{
  travaux: string
  icon: React.ComponentType<{ className?: string }>
  bleu: string
  jaune: string
  violet: string
  rose: string
}> = [
  { travaux: 'Isolation combles', icon: Thermometer, bleu: '25 €/m²', jaune: '20 €/m²', violet: '15 €/m²', rose: '7 €/m²' },
  { travaux: 'Isolation murs ext.', icon: Home, bleu: '75 €/m²', jaune: '60 €/m²', violet: '40 €/m²', rose: '15 €/m²' },
  { travaux: 'PAC air/eau', icon: Snowflake, bleu: '5 000 €', jaune: '4 000 €', violet: '3 000 €', rose: '—' },
  { travaux: 'PAC géothermique', icon: Snowflake, bleu: '11 000 €', jaune: '9 000 €', violet: '6 000 €', rose: '—' },
  { travaux: 'Chaudière granulés', icon: Flame, bleu: '10 000 €', jaune: '8 000 €', violet: '4 000 €', rose: '—' },
  { travaux: 'Fenêtres (par unité)', icon: DoorOpen, bleu: '100 €', jaune: '80 €', violet: '40 €', rose: '—' },
  { travaux: 'Panneaux solaires hybrides', icon: Sun, bleu: '2 500 €', jaune: '2 000 €', violet: '1 000 €', rose: '—' },
  { travaux: 'Audit énergétique', icon: Calculator, bleu: '500 €', jaune: '400 €', violet: '300 €', rose: '—' },
]

// Plafonds de revenus 2026 (Île-de-France)
const PLAFONDS_IDF = [
  { pers: 1, bleu: 23_541, jaune: 28_657, violet: 40_018, rose: '> 40 018' },
  { pers: 2, bleu: 34_551, jaune: 42_058, violet: 58_827, rose: '> 58 827' },
  { pers: 3, bleu: 41_493, jaune: 50_513, violet: 70_382, rose: '> 70 382' },
  { pers: 4, bleu: 48_447, jaune: 58_981, violet: 82_839, rose: '> 82 839' },
  { pers: 5, bleu: 55_427, jaune: 67_473, violet: 94_844, rose: '> 94 844' },
]

function MiniSimulateur() {
  const [personnes, setPersonnes] = useState(2)
  const [revenu, setRevenu] = useState('')

  const rev = parseInt(revenu.replace(/\s/g, ''), 10)
  let categorie = ''
  let color = ''
  if (rev && personnes >= 1 && personnes <= 5) {
    const p = PLAFONDS_IDF[personnes - 1]
    if (rev <= p.bleu) { categorie = 'Bleu (très modeste)'; color = 'text-blue-600 bg-blue-50' }
    else if (rev <= p.jaune) { categorie = 'Jaune (modeste)'; color = 'text-yellow-700 bg-yellow-50' }
    else if (rev <= p.violet) { categorie = 'Violet (intermédiaire)'; color = 'text-violet-600 bg-violet-50' }
    else { categorie = 'Rose (aisé)'; color = 'text-pink-600 bg-pink-50' }
  }

  return (
    <div className="rounded-2xl border border-border bg-white p-6 shadow-md">
      <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900">
        <Calculator className="h-5 w-5 text-primary" />
        Mini-simulateur : votre catégorie
      </h3>
      <p className="mt-1 text-xs text-slate-500">Barèmes Île-de-France 2026 (hors IDF : plafonds inférieurs de ~15 %)</p>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="personnes" className="block text-sm font-medium text-slate-700">Nombre de personnes</label>
          <select
            id="personnes"
            value={personnes}
            onChange={(e) => setPersonnes(Number(e.target.value))}
            className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm"
          >
            {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n} personne{n > 1 ? 's' : ''}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="revenu" className="block text-sm font-medium text-slate-700">Revenu fiscal annuel (€)</label>
          <input
            id="revenu"
            type="text"
            value={revenu}
            onChange={(e) => setRevenu(e.target.value.replace(/[^\d\s]/g, ''))}
            placeholder="Ex: 35 000"
            className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm"
          />
        </div>
      </div>

      {categorie && (
        <div className={`mt-4 rounded-xl p-4 ${color}`}>
          <p className="text-sm font-bold">Votre catégorie : {categorie}</p>
          <p className="mt-1 text-xs">Consultez le tableau ci-dessous pour vos montants d'aide.</p>
        </div>
      )}
    </div>
  )
}

export default function MaPrimeRenovPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Rénovation énergétique', url: 'https://www.afficheservices.fr/renovation-energetique' },
        { name: 'MaPrimeRénov\'', url: 'https://www.afficheservices.fr/maprimenov' },
      ]} />
      <ArticleSchema
        title="MaPrimeRénov' 2026 — Montants, conditions, simulateur"
        description="Guide complet MaPrimeRénov' 2026 : barèmes revalorisés, conditions d'éligibilité, montants par travaux et par revenus."
        url="https://www.afficheservices.fr/maprimenov"
      />
      <FAQSchema items={MPR_FAQS} />

      {/* Hero */}
      <section className="bg-gradient-to-br from-orange-600 via-red-600 to-rose-700 px-4 py-14 text-white sm:py-20">
        <div className="mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-medium text-orange-100">
            <Flame className="h-3.5 w-3.5" /> Guichet rouvert le 23 février 2026
          </div>
          <h1 className="mt-5 text-3xl font-extrabold sm:text-4xl">MaPrimeRénov' 2026 : barèmes, montants, simulateur gratuit</h1>
          <p className="mx-auto mt-4 max-w-2xl text-base text-orange-100 sm:text-lg">
            MaPrimeRénov' 2026 finance jusqu'à 90 % du coût des travaux énergétiques pour les ménages très modestes,
            avec des barèmes spécifiques par travaux (PAC, isolation, fenêtres) et par catégorie de revenus
            (Bleu, Jaune, Violet, Rose). Deux changements majeurs en 2026 : l'isolation des murs n'est plus
            éligible au parcours par geste, et la catégorie Rose accède désormais au parcours accompagné.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3 text-sm text-orange-200">
            <span className="flex items-center gap-1"><CheckCircle className="h-4 w-4" /> Jusqu'à 90 % couverts</span>
            <span className="flex items-center gap-1"><Euro className="h-4 w-4" /> Cumul CEE + éco-PTZ</span>
            <span className="flex items-center gap-1"><Users className="h-4 w-4" /> Occupants et bailleurs</span>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 lg:px-6">

        <div className="mb-6">
          <TrustStrip />
        </div>

        <div className="mb-8">
          <InlineLeadCapture
            heading="Estimez vos aides MaPrimeRénov' 2026"
            subheading="Code postal → 3 devis d'artisans RGE certifiés"
          />
        </div>

        {/* Key Takeaways — AI search optimization, pulled by ChatGPT/Perplexity for summaries */}
        <section className="rounded-2xl border-2 border-orange-200 bg-orange-50/40 p-6">
          <h2 className="text-base font-bold uppercase tracking-wider text-orange-700">À retenir</h2>
          <ul className="mt-3 space-y-2 text-sm leading-relaxed text-slate-700">
            <li className="flex gap-2"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" /> Barèmes 2026 par travaux × revenus : 5 000 € pour une PAC air/eau (Bleu), 25 €/m² pour l'isolation des combles (Bleu), jusqu'à 90 % du coût total pour les ménages très modestes.</li>
            <li className="flex gap-2"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" /> L'isolation des murs (ITE et ITI) et les chaudières biomasse ne sont plus financées dans le parcours par geste depuis le 1er janvier 2026, uniquement via le parcours accompagné.</li>
            <li className="flex gap-2"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" /> La catégorie Rose (revenus supérieurs) est désormais éligible au parcours accompagné, mais reste exclue du parcours par geste.</li>
            <li className="flex gap-2"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" /> Plafond cumulatif d'aide par geste : 20 000 € par logement sur 5 ans.</li>
            <li className="flex gap-2"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" /> Cumulable avec CEE, éco-PTZ et TVA réduite à 5,5 %, ce qui peut couvrir 30 à 90 % du coût total selon les revenus.</li>
          </ul>
        </section>

        {/* Mini simulateur */}
        <div className="mt-12">
          <MiniSimulateur />
        </div>

        {/* NEW: Parcours geste vs accompagné — high SERP relevance for 2026 changes */}
        <section className="mt-12 grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-6">
            <h2 className="text-lg font-bold text-emerald-900">Parcours par geste : interventions ciblées</h2>
            <p className="mt-2 text-sm leading-relaxed text-emerald-800">
              Finance des interventions ponctuelles : changement de chaudière, isolation des combles, pose de fenêtres performantes.
              Idéal pour un projet précis avec un seul corps de métier. L'artisan doit être certifié RGE pour la prestation
              (QualiPAC, Qualibat, Qualifelec).
            </p>
            <ul className="mt-3 space-y-1 text-xs text-emerald-700">
              <li>✓ Pompes à chaleur (air/eau, géothermique)</li>
              <li>✓ Isolation combles, planchers, toiture-terrasse</li>
              <li>✓ Fenêtres double vitrage</li>
              <li>✓ VMC double flux, chauffe-eau thermodynamique</li>
              <li>✓ Audit énergétique</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-blue-200 bg-blue-50/40 p-6">
            <h2 className="text-lg font-bold text-blue-900">Parcours accompagné : rénovations d'ampleur</h2>
            <p className="mt-2 text-sm leading-relaxed text-blue-800">
              Finance les rénovations combinant plusieurs gestes pour gagner au moins 2 classes DPE. Plafonds 2026 :
              30 000 € HT pour 2 classes, 40 000 € HT pour ≥3 classes. Taux jusqu'à 80 % pour les ménages très modestes.
              Accompagnement Mon Accompagnateur Rénov' obligatoire (jusqu'à 2 000 € financés).
            </p>
            <ul className="mt-3 space-y-1 text-xs text-blue-700">
              <li>✓ Audit énergétique préalable obligatoire</li>
              <li>✓ Combinaison de plusieurs gestes (isolation + chauffage + ventilation)</li>
              <li>✓ Catégorie Rose éligible depuis 2026</li>
              <li>✓ Bonus sortie de passoire (+1 000 € Bleu/Jaune)</li>
              <li>✓ Bonus BBC rénovation (+500 €)</li>
            </ul>
          </div>
        </section>

        {/* NEW: What's NOT eligible in 2026 — high SERP value (current event) */}
        <section className="mt-12 rounded-2xl border-2 border-red-200 bg-red-50/40 p-6">
          <h2 className="flex items-center gap-2 text-lg font-bold text-red-900">
            <AlertTriangle className="h-5 w-5" /> Ce qui n'est PLUS éligible en 2026
          </h2>
          <p className="mt-2 text-sm text-red-800">
            Trois changements majeurs ont restreint le parcours par geste depuis le 1er janvier 2026 :
          </p>
          <ol className="mt-3 space-y-2 text-sm text-red-700">
            <li><strong>1. Isolation des murs (ITE et ITI)</strong> : sortie du parcours par geste. Pour bénéficier d'une aide MaPrimeRénov' sur ces postes, passage obligatoire par le parcours accompagné.</li>
            <li><strong>2. Chaudières biomasse</strong> (pellets, granulés bois) : retirées du parcours par geste, sauf en remplacement d'une chaudière fioul ou gaz.</li>
            <li><strong>3. Chaudières gaz à très haute performance énergétique (THPE)</strong> : définitivement sorties du dispositif.</li>
          </ol>
          <p className="mt-3 text-xs text-red-600">
            Ces évolutions reflètent l'orientation gouvernementale vers les rénovations globales et l'électrification du chauffage.
          </p>
        </section>

        {/* Barème par travaux */}
        <section className="mt-12">
          <h2 className="text-xl font-bold text-slate-900">Montants par type de travaux</h2>
          <p className="mt-1 text-sm text-slate-500">Barèmes indicatifs 2026 — le montant dépend de votre catégorie de revenus</p>

          <div className="mt-6 overflow-x-auto">
            <table className="w-full min-w-[600px] text-sm">
              <thead>
                <tr className="border-b border-border bg-slate-50">
                  <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500">Travaux</th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-blue-600">Bleu</th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-yellow-700">Jaune</th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-violet-600">Violet</th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-pink-600">Rose</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {BAREME.map((row) => (
                  <tr key={row.travaux} className="hover:bg-slate-50">
                    <td className="px-3 py-3 font-medium text-slate-900">
                      <span className="flex items-center gap-2"><row.icon className="h-4 w-4 text-orange-500" /> {row.travaux}</span>
                    </td>
                    <td className="px-3 py-3 text-center font-semibold text-blue-600">{row.bleu}</td>
                    <td className="px-3 py-3 text-center font-semibold text-yellow-700">{row.jaune}</td>
                    <td className="px-3 py-3 text-center font-semibold text-violet-600">{row.violet}</td>
                    <td className="px-3 py-3 text-center text-pink-600">{row.rose}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-slate-400">Source : ANAH, barèmes prévisionnels 2026. Montants susceptibles d'évoluer.</p>
        </section>

        {/* Conditions */}
        <section className="mt-12">
          <h2 className="text-xl font-bold text-slate-900">Conditions d'éligibilité</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {[
              { ok: true, text: 'Propriétaire occupant ou bailleur' },
              { ok: true, text: 'Logement de plus de 15 ans (2 ans pour remplacement chaudière fioul)' },
              { ok: true, text: 'Travaux réalisés par un artisan RGE' },
              { ok: true, text: 'Résidence principale (occupant) ou louée (bailleur)' },
              { ok: false, text: 'Demande AVANT signature du devis' },
              { ok: false, text: 'Résidence secondaire exclue' },
            ].map((c, i) => (
              <div key={i} className={`flex items-start gap-2 rounded-lg p-3 ${c.ok ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                {c.ok ? <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />}
                <p className={`text-sm ${c.ok ? 'text-emerald-800' : 'text-amber-800'}`}>{c.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Démarches */}
        <section className="mt-12">
          <h2 className="text-xl font-bold text-slate-900">Les étapes pas à pas</h2>
          <div className="mt-6 space-y-4">
            {[
              { num: '1', title: 'Créez votre compte', desc: 'Sur maprimerenov.gouv.fr avec votre numéro fiscal.' },
              { num: '2', title: 'Faites réaliser un audit', desc: 'Obligatoire pour le parcours « rénovation globale ». Coût : 300-500 € (aidé).' },
              { num: '3', title: 'Demandez des devis RGE', desc: 'Minimum 1 devis d\'artisan RGE. Nous recommandons 3 pour comparer.' },
              { num: '4', title: 'Déposez votre dossier', desc: 'En ligne avec les devis. Accord sous 2-4 semaines.' },
              { num: '5', title: 'Réalisez les travaux', desc: 'L\'artisan RGE effectue les travaux et vous remet la facture.' },
              { num: '6', title: 'Recevez la prime', desc: 'Envoyez la facture en ligne. Virement sous 4-6 semaines.' },
            ].map((step) => (
              <div key={step.num} className="flex items-start gap-4">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orange-100 text-sm font-bold text-orange-700">{step.num}</span>
                <div>
                  <p className="text-sm font-semibold text-slate-900">{step.title}</p>
                  <p className="mt-0.5 text-xs text-slate-500">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* NEW: 3 calculation examples — concrete mini-stories with named persons */}
        <section className="mt-12">
          <h2 className="text-xl font-bold text-slate-900">Comment se calcule le montant exact ? 3 exemples concrets</h2>
          <div className="mt-6 space-y-5">
            <div className="rounded-2xl border border-border bg-white p-6 shadow-xs">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Cas 1 : ménage Jaune (modeste)</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-700">
                <strong>Marie L., infirmière à Lyon</strong>, vit avec son conjoint et ses deux enfants dans une maison de 110 m² datant de 1985.
                Revenu fiscal de référence 52 000 € (catégorie Jaune). Ils installent une PAC air/eau pour remplacer leur chaudière gaz.
                Devis artisan RGE QualiPAC : <strong>14 000 € TTC</strong>.
                MaPrimeRénov' Jaune : <strong>4 000 €</strong>. Prime CEE Coup de Pouce Chauffage : <strong>4 800 €</strong>.
                Reste à charge : 5 200 €, finançable par éco-PTZ à taux zéro sur 15 ans.
              </p>
            </div>
            <div className="rounded-2xl border border-border bg-white p-6 shadow-xs">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Cas 2 : couple Bleu (très modeste)</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-700">
                Couple Bleu (RFR 28 000 €) à Bordeaux souhaitant isoler 90 m² de combles perdus. Calcul : 25 €/m² × 90 m² = <strong>2 250 €</strong> d'aide MaPrimeRénov'.
                Devis isolateur RGE Qualibat : 2 500 € (laine de roche soufflée, R = 7 m²K/W).
                La prime CEE Coup de Pouce Isolation couvre l'intégralité du reste à charge. <strong>Opération quasi gratuite</strong>,
                avec en prime 30 % d'économies de chauffage annuelles.
              </p>
            </div>
            <div className="rounded-2xl border border-border bg-white p-6 shadow-xs">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Cas 3 : foyer Rose (aisé) avec rénovation d'ampleur</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-700">
                Foyer Rose (RFR 95 000 €, 4 personnes) en Île-de-France, appartement de 80 m² classé E au DPE. Objectif : atteindre la classe B
                (gain de 3 classes). Budget 45 000 € HT + audit énergétique 800 €. Le ménage Rose accède au parcours accompagné depuis 2026
                avec un taux d'aide de 30 % plafonné à 40 000 € HT, soit <strong>12 000 €</strong> d'aide MaPrimeRénov'.
                Combiné aux CEE bonifiés (5 000 €), l'éco-PTZ finance le reste sur 20 ans à taux zéro.
              </p>
            </div>
          </div>
        </section>

        {/* NEW: Cumul example with detailed table */}
        <section className="mt-12 rounded-2xl border border-emerald-200 bg-emerald-50/30 p-6">
          <h2 className="text-xl font-bold text-slate-900">Cumul MaPrimeRénov' + CEE + éco-PTZ : exemple chiffré</h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-700">
            Les aides 2026 sont cumulables et se déduisent dans un ordre précis : MaPrimeRénov' en premier, puis CEE, puis éco-PTZ pour le reste.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-slate-700">
            <strong>Cas Thomas et Sandrine</strong>, propriétaires d'une maison de 1972 à Toulouse classée F au DPE. Famille de 4 personnes,
            RFR 38 000 € (Bleu très modeste). Ils remplacent leur chaudière fioul par une PAC air/eau et isolent les combles. Devis total : <strong>20 000 € TTC</strong>.
          </p>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-emerald-200 bg-white text-left text-xs font-semibold text-emerald-700">
                  <th className="px-3 py-2">Poste</th>
                  <th className="px-3 py-2 text-right">Coût</th>
                  <th className="px-3 py-2 text-right">Aide</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-emerald-100 text-slate-700">
                <tr><td className="px-3 py-2">PAC air/eau (RGE QualiPAC)</td><td className="px-3 py-2 text-right">14 000 €</td><td className="px-3 py-2 text-right font-semibold text-emerald-700">MPR Bleu 5 000 € + CEE 5 000 €</td></tr>
                <tr><td className="px-3 py-2">Isolation combles 80 m²</td><td className="px-3 py-2 text-right">2 800 €</td><td className="px-3 py-2 text-right font-semibold text-emerald-700">MPR Bleu 2 000 € + CEE 800 €</td></tr>
                <tr><td className="px-3 py-2">Bonus sortie passoire (F→C)</td><td className="px-3 py-2 text-right text-slate-400">—</td><td className="px-3 py-2 text-right font-semibold text-emerald-700">+1 000 €</td></tr>
                <tr><td className="px-3 py-2">Bonus BBC rénovation</td><td className="px-3 py-2 text-right text-slate-400">—</td><td className="px-3 py-2 text-right font-semibold text-emerald-700">+500 €</td></tr>
                <tr className="bg-emerald-100 font-bold"><td className="px-3 py-2">Total aides</td><td className="px-3 py-2"></td><td className="px-3 py-2 text-right text-emerald-900">14 300 €</td></tr>
                <tr className="font-bold"><td className="px-3 py-2">Reste à charge</td><td className="px-3 py-2 text-right">20 000 €</td><td className="px-3 py-2 text-right text-slate-900">5 700 €</td></tr>
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-emerald-700">
            Avec un éco-PTZ de 30 000 € à taux zéro, ils financent les 5 700 € sur 10 ans sans intérêts.
            <strong> 0 € d'effort de trésorerie initial</strong>, économies de 1 800 €/an sur le chauffage. ROI : 3 ans.
          </p>
        </section>

        {/* NEW: Litige + recours section */}
        <section className="mt-12">
          <h2 className="text-xl font-bold text-slate-900">Que faire en cas de refus ou litige ?</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="text-sm font-bold text-slate-900">Refus de l'Anah</h3>
              <p className="mt-2 text-xs leading-relaxed text-slate-600">
                Recours gracieux auprès du préfet du département dans les 2 mois suivant la notification.
                Motif fréquent (devis signé avant dépôt) : rédhibitoire, ne peut être contourné.
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="text-sm font-bold text-slate-900">Litige avec l'artisan</h3>
              <p className="mt-2 text-xs leading-relaxed text-slate-600">
                AfficheServices propose une <strong>médiation gratuite</strong>. Si elle échoue : Médiateur de la consommation
                (recours obligatoire avant tribunal), DGCCRF en cas de pratique commerciale trompeuse, Médiateur de l'énergie pour litiges PAC.
              </p>
            </div>
          </div>
          <p className="mt-3 text-xs text-slate-500">
            Garde-fou légal : la garantie décennale couvre pendant 10 ans les désordres compromettant la solidité de l'ouvrage ou son usage normal.
          </p>
        </section>

        {/* CTA */}
        <section className="mt-12 rounded-2xl bg-gradient-to-r from-orange-50 to-red-50 p-8 text-center">
          <h2 className="text-xl font-bold text-slate-900">Prêt à rénover ?</h2>
          <p className="mt-2 text-sm text-slate-500">Recevez des devis gratuits d'artisans RGE éligibles MaPrimeRénov'</p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
            <Link to="/demande-devis" className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-bold text-white shadow-md hover:bg-primary-hover btn-press">
              Demander un devis gratuit <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/rge" className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50">
              <Shield className="h-4 w-4" /> Trouver un artisan RGE
            </Link>
          </div>
        </section>
      </div>

      <FloatingCTA label="MaPrimeRénov' : 3 devis RGE" subtitle="Artisans certifiés · sous 48h" whatsappMessage="Bonjour, je m'intéresse à MaPrimeRénov' et souhaite obtenir 3 devis." />
      <Footer />
    </div>
  )
}
